import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { acceptTermsAndPrivacy } from '../../services/termsService';
import toast from 'react-hot-toast';

/**
 * PrivacyPolicyModal Component
 * Modal that requires users to accept the Privacy Policy and Terms of Service
 * before they can use the voice assistant
 */
const PrivacyPolicyModal = ({ isOpen, onClose, onAccept }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!isChecked) {
      toast.error('Please check the box to accept the terms and privacy policy');
      return;
    }

    setIsSubmitting(true);
    try {
      const { success, error } = await acceptTermsAndPrivacy();
      
      if (error || !success) {
        console.error('Error accepting terms:', error);
        toast.error('Failed to record acceptance. Please try again.');
        setIsSubmitting(false);
        return;
      }

      toast.success('Terms accepted successfully!');
      onAccept?.();
      onClose?.();
    } catch (error) {
      console.error('Error in handleAccept:', error);
      toast.error('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsChecked(false);
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiAlertCircle} className="text-blue-600 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-900">
                  Privacy Policy & Terms of Service
                </h2>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Important:</strong> Before you can use our voice assistant, you must review and accept our Privacy Policy and Terms of Service.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 space-y-4">
              {/* Checkbox */}
              <label className="flex items-start space-x-3 cursor-pointer group">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    disabled={isSubmitting}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 cursor-pointer"
                  />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors select-none">
                  I have read and agree to the{' '}
                  <a
                    href="https://www.lifecoachassist.ai/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a
                    href="https://www.lifecoachassist.ai/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Terms of Service
                  </a>.
                </span>
              </label>

              {/* Buttons */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAccept}
                  disabled={!isChecked || isSubmitting}
                  className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheckCircle} className="text-lg" />
                      <span>Accept & Continue</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;
